import formatMoney from "lib/formatMoney";
import dayjs from "dayjs";
import Typography from "@mui/material/Typography";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Link from "@mui/material/Link";

const CalculatorQuestions = [
  {
    q_id: "0",
    q_title: "Loan amount",
    q_description: (answers, descriptionParams) => {
      return (
        <Typography variant="body1">
          What is the loan amount? You can multiply purchase price of the
          property times the loan to value (LTV) of the loan to get this value.
        </Typography>
      );
    },
    node_type: "node",
    a_type: "currency",
    a_input_type: "number",
    a_opts: () => [],
    q_next_step_func: (answer) => answer.q_id + "0",
    q_child_nodes: [0],
    q_validation_func: (answerVal) => {
      return answerVal > 0;
    },
    q_invalid_answer_help_text: "Please enter a value above 0",
    a_error: false,
  },
  {
    q_id: "00",
    q_title: "Loan type",
    q_description: (answers, descriptionParams) => {
      return (
        <>
          <Typography variant="body1" gutterBottom>
            What is the loan type?
          </Typography>
          <List>
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body1">
                    <Link
                      href="https://www.offermarket.us/blog/dscr-loan-vs-conventional-mortgage"
                      target="_blank"
                    >
                      <strong>Conventional - 30 year mortgage:</strong>
                    </Link>{" "}
                    The most common type of residential mortgages used by most
                    people who plan to live in the property as a primary
                    resident.
                  </Typography>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body1">
                    <Link
                      href="https://www.offermarket.us/dscr-loan"
                      target="_blank"
                    >
                      <strong>Debt service coverage ratio (DSCR) loan:</strong>
                    </Link>{" "}
                    A loan used by landlords to finance acquisition of
                    properties in which they are not planning on residing in.
                  </Typography>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body1">
                    <Link
                      href="https://www.offermarket.us/fix-and-rent-loan"
                      target="_blank"
                    >
                      <strong>Fix and rent:</strong>
                    </Link>{" "}
                    A dual loan type, where first loan is a rehab loan that
                    allows the borrower to fix up the property and the second
                    loan is a DSCR refinance loan after rehab is complete (with
                    significantly discounted process costs due to this packaged
                    approach).
                  </Typography>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body1">
                    <Link
                      href="https://www.offermarket.us/blog/ground-up-construction"
                      target="_blank"
                    >
                      <strong>Build to rent:</strong>
                    </Link>{" "}
                    Similar to fix and rent, but first loan is a ground up
                    construction loan instead of a rehab loan.
                  </Typography>
                }
              />
            </ListItem>
          </List>
        </>
      );
    },
    node_type: "node",
    a_type: "mc",
    a_opts: (answers) => {
      return [
        "Conventional - 30 year mortgage",
        "Non-QM - primary residence",
        "DSCR",
        "Slow flip",
        "Fix and rent", //UPDATE, used to be "Fix and flip", now "Fix and rent"
        // "Short-term rental",
        "Build to rent",
      ];
    },
    q_next_step_func: (answer, answers, questions) => {
      // We don't do short term, so ok to boot them here
      // UPDATE 06/17/2023 we now do short term, mvp treat like DSCR
      // if (answer.a_string === "Short-term rental") {
      //   return answer.q_id + "4";
      // }

      return answer.q_id + "0";
    },

    q_child_nodes: [0, 1],
    q_validation_func: (answerVal) => {
      // this MC so any option clicked is valid
      return true; //isValid
    },
    q_invalid_answer_help_text: "",
    a_error: false,
  },

  {
    q_id: "000",
    q_title: "Amortization type",
    q_description: (answers, descriptionParams) => {
      return (
        <Typography variant="body1">
          What is the amortization type of the loan?
        </Typography>
      );
    },
    node_type: "node",
    a_type: "mc",
    a_opts: (answers) => {
      return ["30-year", "15-year", "20-year", "Other"];
    },
    q_next_step_func: (answer) => answer.q_id + "0",
    q_child_nodes: [0],
    q_validation_func: (answerVal) => {
      return answerVal !== "";
    },
    q_invalid_answer_help_text: "Please select an amortization type",
    a_error: false,
  },
  {
    q_id: "0000",
    q_title: "Term",
    q_description: (answers, descriptionParams) => {
      return (
        <Typography variant="body1">What is the term of the loan?</Typography>
      );
    },
    node_type: "node",
    a_type: "number",
    a_input_type: "number",
    a_opts: () => [],
    q_next_step_func: (answer) => answer.q_id + "0",
    q_child_nodes: [0],
    q_validation_func: (answerVal) => {
      return answerVal > 0;
    },
    q_invalid_answer_help_text: "Please enter a value above 0",
    a_error: false,
  },
  {
    q_id: "00000",
    q_title: "Interest rate",
    q_description: (answers, descriptionParams) => {
      return (
        <Typography variant="body1">
          What is the interest rate of the loan?
        </Typography>
      );
    },
    node_type: "node",
    a_type: "number",
    a_input_type: "number",
    a_opts: () => [],
    q_next_step_func: (answer) => answer.q_id + "0",
    q_child_nodes: [0],
    q_validation_func: (answerVal) => {
      return answerVal > 0;
    },
    q_invalid_answer_help_text: "Please enter a value above 0",
    a_error: false,
  },
  {
    q_id: "000000",
    q_title: "Property tax",
    q_description: (answers, descriptionParams) => {
      return (
        <Typography variant="body1">
          What is the estimated property tax for the property?
        </Typography>
      );
    },
    node_type: "node",
    a_type: "currency",
    a_input_type: "number",
    a_opts: () => [],
    q_next_step_func: (answer) => answer.q_id + "0",
    q_child_nodes: [0],
    q_validation_func: (answerVal) => {
      return answerVal >= 0;
    },
    q_invalid_answer_help_text: "Please enter a value of 0 or greater",
    a_error: false,
  },
  {
    q_id: "0000000",
    q_title: "Insurance",
    q_description: (answers, descriptionParams) => {
      return (
        <Typography variant="body1">
          What is the estimated insurance for the property?
        </Typography>
      );
    },
    node_type: "node",
    a_type: "currency",
    a_input_type: "number",
    a_opts: () => [],
    q_next_step_func: (answer) => answer.q_id + "0",
    q_child_nodes: [0],
    q_validation_func: (answerVal) => {
      return answerVal >= 0;
    },
    q_invalid_answer_help_text: "Please enter a value of 0 or greater",
    a_error: false,
  },
  {
    q_id: "00000000",
    q_title: "Payment frequency",
    q_description: (answers, descriptionParams) => {
      return (
        <Typography variant="body1">
          What is the payment frequency of the loan?
        </Typography>
      );
    },
    node_type: "node",
    a_type: "mc",
    a_opts: (answers) => {
      return ["Monthly", "Bi-weekly", "Weekly", "Other"];
    },
    q_next_step_func: (answer) => answer.q_id + "0",
    q_child_nodes: [0],
    q_validation_func: (answerVal) => {
      return answerVal !== "";
    },
    q_invalid_answer_help_text: "Please select a payment frequency",
    a_error: false,
  },
  {
    q_id: "000000000",
    q_title: "Origination fee",
    q_description: (answers, descriptionParams) => {
      const loanType =
        answers.find((a) => a.q_title === "Loan type")?.a_string ||
        "Conventional - 30 year mortgage";
      const loanAmount =
        parseFloat(
          answers.find((a) => a.q_title === "Loan amount")?.a_string
        ) || 0;
      const presets = descriptionParams?.loanTypeLineItemPresets?.[loanType];
      const fee = presets?.["Origination fee"]?.(loanAmount) || 0;

      return (
        <>
          <Typography variant="body1" gutterBottom>
            What is the loan origination fee? This is typically charged by the
            lender as a percentage of the loan amount.
          </Typography>
          <Typography variant="body1">
            For {loanType}, this is typically{" "}
            <Typography component="span" variant="body1" fontWeight="bold">
              {((fee / loanAmount) * 100).toFixed(1)}%
            </Typography>{" "}
            of the loan amount.
          </Typography>
        </>
      );
    },
    node_type: "node",
    a_type: "currency",
    a_input_type: "number",
    a_opts: () => [],
    q_next_step_func: (answer) => answer.q_id + "0",
    q_child_nodes: [0],
    q_validation_func: (answerVal) => answerVal >= 0,
    q_invalid_answer_help_text: "Please enter a value of 0 or greater",
    a_error: false,
  },
  {
    q_id: "0000000000",
    q_title: "Loan fees",
    q_description: (answers, descriptionParams) => {
      const loanType =
        answers.find((a) => a.q_title === "Loan type")?.a_string ||
        "Conventional - 30 year mortgage";
      const loanAmount =
        parseFloat(
          answers.find((a) => a.q_title === "Loan amount")?.a_string
        ) || 0;
      const presets = descriptionParams?.loanTypeLineItemPresets?.[loanType];
      const fee = presets?.["Loan fees"]?.(loanAmount) || 0;

      return (
        <>
          <Typography variant="body1" gutterBottom>
            What are the additional loan fees? These fees cover various
            administrative and processing costs:
          </Typography>
          <List>
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body1">
                    • Processing fees for handling your loan application
                  </Typography>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body1">
                    • Underwriting fees for evaluating your loan request
                  </Typography>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body1">
                    • Document preparation and review fees
                  </Typography>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body1">
                    • Credit report and verification fees
                  </Typography>
                }
              />
            </ListItem>
          </List>
          <Typography variant="body1">
            For {loanType}, this is typically{" "}
            <Typography component="span" variant="body1" fontWeight="bold">
              ${formatMoney(fee, 0)}
            </Typography>
            .
          </Typography>
        </>
      );
    },
    node_type: "node",
    a_type: "currency",
    a_input_type: "number",
    a_opts: () => [],
    q_next_step_func: (answer) => answer.q_id + "0",
    q_child_nodes: [0],
    q_validation_func: (answerVal) => answerVal >= 0,
    q_invalid_answer_help_text: "Please enter a value of 0 or greater",
    a_error: false,
    a_string: "1995", // Default value
  },
  {
    q_id: "00000000000",
    q_title: "Appraisal",
    q_description: (answers, descriptionParams) => {
      const loanType =
        answers.find((a) => a.q_title === "Loan type")?.a_string ||
        "Conventional - 30 year mortgage";
      const loanAmount =
        parseFloat(
          answers.find((a) => a.q_title === "Loan amount")?.a_string
        ) || 0;
      const presets = descriptionParams?.loanTypeLineItemPresets?.[loanType];
      const fee = presets?.["Appraisal"]?.(loanAmount) || 0;

      return (
        <>
          <Typography variant="body1" gutterBottom>
            A fee paid to a third party, certified appraiser to perform and
            appraisal of the property to establish an appraised value.
          </Typography>
          <Typography variant="body1">
            For {loanType}, this is typically{" "}
            <Typography component="span" variant="body1" fontWeight="bold">
              ${formatMoney(fee, 0)}
            </Typography>
            .
          </Typography>
        </>
      );
    },
    node_type: "node",
    a_type: "currency",
    a_input_type: "number",
    a_opts: () => [],
    q_next_step_func: (answer) => answer.q_id + "0",
    q_child_nodes: [0],
    q_validation_func: (answerVal) => {
      return answerVal >= 0;
    },
    q_invalid_answer_help_text: "Please enter a value of 0 or greater",
    a_error: false,
  },
  {
    q_id: "000000000000",
    q_title: "Title fees",
    q_description: (answers, descriptionParams) => {
      const loanType =
        answers.find((a) => a.q_title === "Loan type")?.a_string ||
        "Conventional - 30 year mortgage";
      const loanAmount =
        parseFloat(
          answers.find((a) => a.q_title === "Loan amount")?.a_string
        ) || 0;
      const presets = descriptionParams?.loanTypeLineItemPresets?.[loanType];
      const fee = presets?.["Title fees"]?.(loanAmount) || 0;

      return (
        <>
          <Typography variant="body1" gutterBottom>
            What are the estimated title fees? These fees ensure clear property
            ownership and protect your interests:
          </Typography>
          <List>
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body1">
                    • Title search to verify property ownership history
                  </Typography>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body1">
                    • Title insurance to protect against ownership disputes
                  </Typography>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body1">
                    • Recording fees for official document filing
                  </Typography>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body1">
                    • Settlement and closing service fees
                  </Typography>
                }
              />
            </ListItem>
          </List>
          <Typography variant="body1">
            For {loanType}, this is typically{" "}
            <Typography component="span" variant="body1" fontWeight="bold">
              {((fee / loanAmount) * 100).toFixed(1)}%
            </Typography>{" "}
            of the loan amount.
          </Typography>
        </>
      );
    },
    node_type: "node",
    a_type: "currency",
    a_input_type: "number",
    a_opts: () => [],
    q_next_step_func: (answer) => answer.q_id + "0",
    q_child_nodes: [0],
    q_validation_func: (answerVal) => answerVal >= 0,
    q_invalid_answer_help_text: "Please enter a value of 0 or greater",
    a_error: false,
  },
  {
    q_id: "0000000000000",
    q_title: "Escrow",
    q_description: (answers, descriptionParams) => {
      const loanType =
        answers.find((a) => a.q_title === "Loan type")?.a_string ||
        "Conventional - 30 year mortgage";
      const loanAmount =
        parseFloat(
          answers.find((a) => a.q_title === "Loan amount")?.a_string
        ) || 0;
      const presets = descriptionParams?.loanTypeLineItemPresets?.[loanType];
      const fee = presets?.["Escrow"]?.(loanAmount) || 0;

      return (
        <>
          <Typography variant="body1" gutterBottom>
            What are the estimated escrow fees? These fees cover the services of
            a neutral third party who handles the transaction:
          </Typography>
          <List>
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body1">
                    • Escrow company service fees
                  </Typography>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body1">
                    • Initial escrow deposits for property taxes
                  </Typography>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body1">
                    • Initial escrow deposits for insurance
                  </Typography>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body1">
                    • Wire transfer and document handling fees
                  </Typography>
                }
              />
            </ListItem>
          </List>
          <Typography variant="body1">
            For {loanType}, this is typically{" "}
            <Typography component="span" variant="body1" fontWeight="bold">
              {((fee / loanAmount) * 100).toFixed(1)}%
            </Typography>{" "}
            of the loan amount.
          </Typography>
        </>
      );
    },
    node_type: "node",
    a_type: "currency",
    a_input_type: "number",
    a_opts: () => [],
    q_next_step_func: (answer) => answer.q_id + "0",
    q_child_nodes: [0],
    q_validation_func: (answerVal) => answerVal >= 0,
    q_invalid_answer_help_text: "Please enter a value of 0 or greater",
    a_error: false,
  },
  {
    q_id: "00000000000000",
    q_title: "Prepaid interest",
    q_description: (answers, descriptionParams) => {
      const loanType =
        answers.find((a) => a.q_title === "Loan type")?.a_string ||
        "Conventional - 30 year mortgage";
      const loanAmount =
        parseFloat(
          answers.find((a) => a.q_title === "Loan amount")?.a_string
        ) || 0;
      const presets = descriptionParams?.loanTypeLineItemPresets?.[loanType];
      const fee = presets?.["Prepaid interest"]?.(loanAmount) || 0;

      return (
        <>
          <Typography variant="body1" gutterBottom>
            What is the estimated prepaid interest? This covers the interest
            that accrues between closing and your first payment:
          </Typography>
          <List>
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body1">
                    • Interest from closing date to first payment date
                  </Typography>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body1">
                    • Calculated based on your loan amount and interest rate
                  </Typography>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body1">
                    • Varies depending on closing date and payment schedule
                  </Typography>
                }
              />
            </ListItem>
          </List>
          <Typography variant="body1">
            For {loanType}, this is typically{" "}
            <Typography component="span" variant="body1" fontWeight="bold">
              {((fee / loanAmount) * 100).toFixed(1)}%
            </Typography>{" "}
            of the loan amount.
          </Typography>
        </>
      );
    },
    node_type: "node",
    a_type: "currency",
    a_input_type: "number",
    a_opts: () => [],
    q_next_step_func: (answer) => answer.q_id + "0",
    q_child_nodes: [0],
    q_validation_func: (answerVal) => answerVal >= 0,
    q_invalid_answer_help_text: "Please enter a value of 0 or greater",
    a_error: false,
  },
  {
    q_id: "000000000000000",
    q_title: "Prepaid insurance",
    q_description: (answers, descriptionParams) => {
      const loanType =
        answers.find((a) => a.q_title === "Loan type")?.a_string ||
        "Conventional - 30 year mortgage";
      const loanAmount =
        parseFloat(
          answers.find((a) => a.q_title === "Loan amount")?.a_string
        ) || 0;
      const presets = descriptionParams?.loanTypeLineItemPresets?.[loanType];
      const fee = presets?.["Prepaid insurance"]?.(loanAmount) || 0;

      return (
        <>
          <Typography variant="body1" gutterBottom>
            What is the estimated prepaid insurance? This covers your initial
            insurance premium:
          </Typography>
          <List>
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body1">
                    • First year's property insurance premium
                  </Typography>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body1">
                    • Required to protect the property from damage
                  </Typography>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body1">
                    • May include flood insurance if required
                  </Typography>
                }
              />
            </ListItem>
          </List>
          <Typography variant="body1">
            For {loanType}, this is typically{" "}
            <Typography component="span" variant="body1" fontWeight="bold">
              {((fee / loanAmount) * 100).toFixed(1)}%
            </Typography>{" "}
            of the loan amount.
          </Typography>
        </>
      );
    },
    node_type: "node",
    a_type: "currency",
    a_input_type: "number",
    a_opts: () => [],
    q_next_step_func: (answer) => answer.q_id + "0",
    q_child_nodes: [0],
    q_validation_func: (answerVal) => answerVal >= 0,
    q_invalid_answer_help_text: "Please enter a value of 0 or greater",
    a_error: false,
  },
  {
    q_id: "0000000000000000",
    q_title: "Calculation name",
    q_description: (answers, descriptionParams) => {
      return (
        <Typography variant="body1">
          What would you like to name this calculation? Ex. 'Land loan calc for
          123 Acre Lot'
        </Typography>
      );
    },
    node_type: "node",
    a_type: "text",
    a_input_type: "text",
    a_opts: () => [],
    q_next_step_func: (answer) => answer.q_id + "0",
    q_child_nodes: [0],
    q_validation_func: () => true,
    q_invalid_answer_help_text: "",
    a_error: false,
  },
  {
    q_id: "00000000000000000",
    q_title: "Address (optional)",
    q_description: (answers, descriptionParams) => {
      return (
        <Typography variant="body1">
          Enter property address to help organize your calculations. You can
          also enter a state, zip or nothing, since this field is optional.
        </Typography>
      );
    },
    node_type: "node",
    a_type: "address",
    a_opts: () => [],
    q_next_step_func: (answer) => answer.q_id + "0",
    q_child_nodes: [0],
    q_validation_func: (answerVal) => {
      if (!answerVal || answerVal.length === 0) return true;
      return answerVal.length > 0;
    },
    q_invalid_answer_help_text: "Please enter or select an address",
    a_error: false,
  },
];

export default CalculatorQuestions;
